import React, { useState, useEffect } from 'react';
import { MapContainer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { FiX, FiSearch } from 'react-icons/fi';
import { BasemapLayers, BasemapToggle } from './MapBasemaps';
import { API_URL } from '../config';
import 'leaflet/dist/leaflet.css';

const GA_DEFAULT = [34.30, -83.80]; // North Georgia fallback

const pinIcon = L.divIcon({
  className: 'loc-pin',
  html: '<div style="background:#dc2626;width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,.45)"></div>',
  iconSize: [22, 22], iconAnchor: [11, 22],
});

function ClickCapture({ onPick }) {
  useMapEvents({ click: (e) => onPick(e.latlng.lat, e.latlng.lng) });
  return null;
}
function Recenter({ center, zoom }) {
  const map = useMap();
  useEffect(() => { if (center) map.setView(center, zoom || map.getZoom()); }, [center, zoom, map]);
  return null;
}

// With onPick, the pin is handed back to the caller (e.g. an unsaved quote form)
// instead of being saved straight onto a job.
export default function JobLocationPickerModal({ jobId, job, onClose, onSaved, onPick, title }) {
  const initLat = job?.latitude != null ? Number(job.latitude) : null;
  const initLng = job?.longitude != null ? Number(job.longitude) : null;
  const [lat, setLat] = useState(initLat);
  const [lng, setLng] = useState(initLng);
  const [center, setCenter] = useState(initLat != null ? [initLat, initLng] : GA_DEFAULT);
  const [basemap, setBasemap] = useState('satellite');
  const [busy, setBusy] = useState(false);
  const [geo, setGeo] = useState(false);
  const [err, setErr] = useState('');

  const token = localStorage.getItem('authToken');
  const pick = (la, ln) => { setLat(la); setLng(ln); setErr(''); };

  const findFromAddress = async () => {
    const addr = [job?.property_address, job?.location_city_state_zip, job?.county ? `${job.county} County` : null, 'GA']
      .filter(Boolean).join(', ');
    if (!job?.property_address && !job?.location_city_state_zip) { setErr(onPick ? 'Enter a property address first to look it up.' : 'No property address on this job to look up.'); return; }
    setGeo(true); setErr('');
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addr)}&limit=1`;
      const res = await fetch(url);
      const results = await res.json();
      if (results && results.length) {
        const la = parseFloat(results[0].lat), ln = parseFloat(results[0].lon);
        setLat(la); setLng(ln); setCenter([la, ln]);
      } else {
        setErr('Address not found — click the map to set the location manually.');
      }
    } catch (e) {
      setErr('Lookup failed — click the map to set the location.');
    }
    setGeo(false);
  };

  const save = async () => {
    if (lat == null || lng == null || isNaN(lat) || isNaN(lng)) { setErr('Drop a pin (or look up the address) first.'); return; }
    if (onPick) { onPick(Number(lat).toFixed(6), Number(lng).toFixed(6)); onClose(); return; }
    setBusy(true); setErr('');
    try {
      await axios.put(`${API_URL}/jobs/${jobId}`,
        { latitude: Number(lat).toFixed(6), longitude: Number(lng).toFixed(6) },
        { headers: { Authorization: `Bearer ${token}` } });
      if (onSaved) onSaved();
      onClose();
    } catch (e) {
      setErr('Could not save the location.');
      setBusy(false);
    }
  };

  const numInput = { width: '100%', padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, boxSizing: 'border-box' };

  return (
    <div onClick={() => !busy && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000, padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 660, padding: 18, maxHeight: '92vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontSize: 18, color: '#0f172a' }}>{title || 'Set job location'}</h3>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><FiX /></button>
        </div>
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10 }}>Click the map to drop a pin, drag it to fine-tune, or look it up from the property address.</div>

        <div style={{ position: 'relative', height: 360, borderRadius: 10, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
          <MapContainer center={center} zoom={initLat != null ? 16 : 8} style={{ height: '100%', width: '100%' }}>
            <BasemapLayers basemap={basemap} />
            <ClickCapture onPick={pick} />
            <Recenter center={center} zoom={16} />
            {lat != null && lng != null && !isNaN(lat) && !isNaN(lng) && (
              <Marker position={[lat, lng]} icon={pinIcon} draggable
                eventHandlers={{ dragend: (e) => { const p = e.target.getLatLng(); pick(p.lat, p.lng); } }} />
            )}
          </MapContainer>
          <BasemapToggle basemap={basemap} setBasemap={setBasemap} />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
          <button type="button" onClick={findFromAddress} disabled={geo} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
            <FiSearch size={14} /> {geo ? 'Looking up…' : 'Find from address'}
          </button>
          <div style={{ display: 'flex', gap: 8, flex: 1, minWidth: 220 }}>
            <input type="number" step="any" placeholder="Latitude" value={lat == null ? '' : lat}
              onChange={(e) => setLat(e.target.value === '' ? null : parseFloat(e.target.value))} style={numInput} />
            <input type="number" step="any" placeholder="Longitude" value={lng == null ? '' : lng}
              onChange={(e) => setLng(e.target.value === '' ? null : parseFloat(e.target.value))} style={numInput} />
          </div>
        </div>

        {err && <div style={{ color: '#dc2626', fontSize: 13, marginTop: 8 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button type="button" onClick={onClose} disabled={busy} style={{ flex: 1, padding: 12, background: '#f1f5f9', color: '#334155', border: '1px solid #e2e8f0', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button type="button" onClick={save} disabled={busy || lat == null} style={{ flex: 2, padding: 12, background: '#16a34a', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: (busy || lat == null) ? 0.6 : 1 }}>{busy ? 'Saving…' : (onPick ? 'Use This Location' : 'Save Location')}</button>
        </div>
      </div>
    </div>
  );
}
